import {desktopHandoffReturnPath, existingAccountPath, signupPlanId, signupSetupPath} from '../src/signup-route';
import {useEffect, useId, useRef, useState, useSyncExternalStore, type FormEvent} from 'react';
import {EMAIL_PASSWORD_POLICY} from '../src/auth-policy';
import {emailSignInPolicy} from '../src/email-sign-in-policy';
import {ErrorNotice, Loading, shortId} from './components';
import {APP_BASE, Link, navigate, useSearch} from './router';
import type {AuthMode, ErrorDetails, HermesState} from './lib/types';
import {errorDetails, type hermesActions} from './lib/store';
import {onboardingDestination} from './lib/onboarding';
import {registrationDraftSnapshot, subscribeRegistrationDraft, updateRegistrationDraft} from './lib/registration-draft';
import './auth.css';
import {platform, type PendingEmailCode} from './platform/transport';

type Props = {state: HermesState; actions: typeof hermesActions};
export type {AuthMode} from './lib/types';

function isAuthPath(path: string): boolean {
  return ['login', 'register', 'setup-login'].some(page => path === `${APP_BASE}/${page}`);
}

function requestedDestination(search: string, returnTo?: string): string {
  const parameters = new URLSearchParams(search);
  const directPlan = signupPlanId(`${APP_BASE}/register${search}`, APP_BASE);
  const requested = returnTo ?? parameters.get('next') ?? (directPlan ? signupSetupPath(directPlan, `${APP_BASE}/register${search}`, APP_BASE) : `${APP_BASE}/workspace`);
  const target = new URL(requested, window.location.origin);
  if (target.origin !== window.location.origin || !target.pathname.startsWith(`${APP_BASE}/`) ||
      isAuthPath(target.pathname)) {
    throw new Error('The sign-in return address must be a page in EightBots.');
  }
  return `${target.pathname}${target.search}`;
}

export function AccountIdentity({state, actions}: Props) {
  const authenticated = state.session.data;
  if (!authenticated) return null;
  return <div className="account-identity"><span>{authenticated.authMethod === 'google' ? 'Signed in with Google' : authenticated.authMethod === 'discord' ? 'Signed in with Discord' : 'Signed in'}</span>
    <strong>{authenticated.email ?? (authenticated.address === null ? authenticated.accountId : shortId(authenticated.address))}</strong>
    <button type="button" className="text-button" disabled={state.operation.label !== null}
      onClick={() => void actions.signOut().catch(() => undefined)}>Sign out</button>
  </div>;
}

export function AuthPage(props: Props & {mode: AuthMode; returnTo?: string | undefined}) {
  return platform().kind === 'desktop' ? <DesktopSignIn {...props}/> : <BrowserAuthPage {...props}/>;
}

function DesktopSignIn({state, actions, mode, returnTo}: Props & {mode: AuthMode; returnTo?: string | undefined}) {
  const fieldId = useId();
  const search = useSearch();
  const destination = requestedDestination(search, returnTo && isAuthPath(new URL(returnTo, window.location.origin).pathname) ? undefined : returnTo);
  const busy = state.operation.label !== null;
  const registering = mode === 'register';
  const [signInMethod, setSignInMethod] = useState<'email-code' | 'password'>('password');
  const [pendingError, setPendingError] = useState<ErrorDetails | null>(null);
  const registrationDraft = useSyncExternalStore(subscribeRegistrationDraft, registrationDraftSnapshot);
  const next = `?${new URLSearchParams({next: destination})}`;
  useEffect(() => {
    if (mode !== 'login') return;
    let current = true;
    void actions.pendingEmailCode().then(pending => {if (current && pending) setSignInMethod('email-code');})
      .catch(error => {if (current) setPendingError(errorDetails(error));});
    return () => {current = false;};
  }, [actions, mode]);
  useEffect(() => {
    const authenticated = state.session.data;
    if (authenticated && !busy && ['/login', '/register'].some(path => window.location.pathname === `${APP_BASE}${path}`)) {
      navigate(authenticated.onboarding.workspaceAdmitted ? existingAccountPath(destination, APP_BASE)
        : onboardingDestination(authenticated, destination), true);
    }
  }, [state.session.data, busy, destination]);
  function signedIn(authenticated: NonNullable<HermesState['session']['data']>) {
    navigate(authenticated.onboarding.workspaceAdmitted ? existingAccountPath(destination, APP_BASE)
      : onboardingDestination(authenticated, destination), true);
  }
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      const authenticate = registering ? actions.register : actions.signIn;
      const authenticated = await authenticate(String(data.get('email') ?? ''), String(data.get('password') ?? ''));
      form.reset();
      signedIn(authenticated);
    } catch { /* The shared operation error is shown below. */ }
  }
  return <section className="auth-page" aria-labelledby={`${fieldId}-title`}><div className="auth-content">
    <header><h1 id={`${fieldId}-title`}>{registering ? 'Create an account' : 'Sign in to EightBots'}</h1></header>
    {mode === 'login' && <div className="auth-switch" aria-label="Sign-in method">
      <button type="button" className="text-button" disabled={busy} aria-pressed={signInMethod === 'password'} onClick={() => setSignInMethod('password')}>Password</button>{' · '}
      <button type="button" className="text-button" disabled={busy} aria-pressed={signInMethod === 'email-code'} onClick={() => setSignInMethod('email-code')}>Email code</button>
    </div>}
    <ErrorNotice error={pendingError}/>
    {mode === 'setup-login' ? <p>Password setup for an existing account is not available in this app.</p>
      : mode === 'login' && signInMethod === 'email-code' ? <><DesktopEmailSignIn state={state} actions={actions} fieldId={fieldId} onSignedIn={signedIn}/>
        <p className="auth-switch">Need an account? <Link href={`${APP_BASE}/register${next}`}>Sign up</Link></p></>
      : <form key={mode} className="auth-form" aria-busy={busy} onSubmit={event => void submit(event)}>
        <EmailPasswordFields fieldId={fieldId} busy={busy} newPassword={registering} registrationDraft={registering ? registrationDraft : null}/>
        {registering && <div className="auth-consent"><input id={`${fieldId}-terms`} type="checkbox" name="terms" required disabled={busy}
          checked={registrationDraft.termsAccepted} onChange={event => updateRegistrationDraft({termsAccepted: event.target.checked})}/>
          <label htmlFor={`${fieldId}-terms`}>I agree to the <TermsLink/>, and acknowledge EightBots’ <PrivacyLink/></label></div>}
        <ErrorNotice error={state.operation.scope === mode ? state.operation.error : null}/>
        <button className="button auth-submit" type="submit" disabled={busy}>{registering ? 'Sign up' : 'Sign in'}</button>
        <p className="auth-switch">{registering ? 'Already registered?' : 'Need an account?'}{' '}
          <Link href={`${APP_BASE}/${registering ? 'login' : 'register'}${next}`}>{registering ? 'Sign in' : 'Sign up'}</Link></p>
      </form>}
  </div></section>;
}

function DesktopEmailSignIn({state, actions, fieldId, onSignedIn}: Props & {
  fieldId: string; onSignedIn: (session: NonNullable<HermesState['session']['data']>) => void;
}) {
  const [email, setEmail] = useState(''), [code, setCode] = useState('');
  const [pending, setPending] = useState<{challenge: PendingEmailCode; receivedAt: number} | null>(null);
  const [loading, setLoading] = useState(true), [loadError, setLoadError] = useState<ErrorDetails | null>(null);
  const [now, setNow] = useState(Date.now);
  const busy = loading || state.operation.label !== null;
  function receive(challenge: PendingEmailCode | null) {
    const receivedAt = Date.now();
    setPending(challenge ? {challenge, receivedAt} : null);
    setNow(receivedAt);
    if (challenge) setEmail(challenge.email);
  }
  useEffect(() => {
    let current = true;
    void actions.pendingEmailCode().then(challenge => {if (current) receive(challenge);})
      .catch(error => {if (current) setLoadError(errorDetails(error));})
      .finally(() => {if (current) setLoading(false);});
    return () => {current = false;};
  }, [actions]);
  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [pending]);
  const expiresAt = pending ? Date.parse(pending.challenge.expiresAt) : null;
  const expired = expiresAt !== null && now >= expiresAt;
  const retrySeconds = pending ? Math.max(0, Math.ceil((pending.receivedAt + pending.challenge.retryAfterSeconds * 1000 - now) / 1000)) : 0;
  async function sendCode() {
    if (busy) return;
    try {receive(await actions.startEmailCode(email)); setCode(''); setLoadError(null);}
    catch { /* The shared operation retains the complete error. */ }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!pending) {await sendCode(); return;}
    try {onSignedIn(await actions.finishEmailCode(code));}
    catch { /* The shared operation retains the complete error. */ }
  }
  return <form className="auth-form" aria-busy={busy} onSubmit={event => void submit(event)}>
    <Loading active={loading}/>
    {pending ? <>
      <p>Enter the {emailSignInPolicy.codeDigits}-digit code sent to {pending.challenge.email}.</p>
      <div className="auth-field"><label htmlFor={`${fieldId}-code`}>Sign-in code</label>
        <input id={`${fieldId}-code`} name="code" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
          required pattern={`[0-9]{${emailSignInPolicy.codeDigits}}`} maxLength={emailSignInPolicy.codeDigits}
          value={code} onChange={event => setCode(event.target.value.replace(/\s/g, ''))} disabled={busy || expired}/></div>
      <p role="status">{expired ? 'This code has expired. Request a new code.' : <>The code expires at <time dateTime={pending.challenge.expiresAt}>{new Date(pending.challenge.expiresAt).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'})}</time>.</>}</p>
      <button type="submit" className="button auth-submit" disabled={busy || expired}>Sign in</button>
      <p className="auth-switch"><button type="button" className="text-button" disabled={busy || retrySeconds > 0} onClick={() => void sendCode()}>
        {retrySeconds > 0 ? `Send a new code in ${retrySeconds}s` : 'Send a new code'}</button></p>
      <p className="auth-switch"><button type="button" className="text-button" disabled={busy} onClick={() => {
        void actions.cancelEmailCode().then(() => {receive(null); setCode('');}).catch(() => undefined);
      }}>Use another email</button></p>
    </> : <>
      <p>For accounts created with Google or Discord. Use your EightBots account email. If you set an account password, select Password.</p>
      <div className="auth-field"><label htmlFor={`${fieldId}-code-email`}>Email</label><input id={`${fieldId}-code-email`} name="email" type="email"
        autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} required maxLength={EMAIL_PASSWORD_POLICY.emailMaxLength}
        value={email} onChange={event => setEmail(event.target.value)} disabled={busy}/></div>
      <button type="submit" className="button auth-submit" disabled={busy}>Send sign-in code</button>
    </>}
    <ErrorNotice error={loadError}/>
    <ErrorNotice error={state.operation.scope === 'login' ? state.operation.error : null}/>
  </form>;
}

function EmailPasswordFields({fieldId, busy, newPassword, registrationDraft}: {
  fieldId: string; busy: boolean; newPassword: boolean; registrationDraft: ReturnType<typeof registrationDraftSnapshot> | null;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  return <>
    <div className="auth-field"><label htmlFor={`${fieldId}-email`}>Email</label><input id={`${fieldId}-email`} type="email" name="email" autoComplete="username" autoCapitalize="none"
      autoCorrect="off" spellCheck={false} maxLength={EMAIL_PASSWORD_POLICY.emailMaxLength} required disabled={busy}
      value={registrationDraft?.email}
      onChange={registrationDraft ? event => updateRegistrationDraft({email: event.target.value}) : undefined}/></div>
    <div className="auth-field"><label htmlFor={`${fieldId}-password`}>Password</label>
      <div className="auth-password"><input id={`${fieldId}-password`} type={showPassword ? 'text' : 'password'} name="password"
        autoComplete={newPassword ? 'new-password' : 'current-password'} required disabled={busy}
        value={registrationDraft?.password}
        onChange={registrationDraft ? event => updateRegistrationDraft({password: event.target.value}) : undefined}
        minLength={newPassword ? EMAIL_PASSWORD_POLICY.minLength : undefined} maxLength={EMAIL_PASSWORD_POLICY.maxLength}
        onFocus={() => setPasswordFocused(true)} onBlur={() => setPasswordFocused(false)}
        aria-describedby={newPassword ? `${fieldId}-password-help` : undefined}/>
        <button type="button" className="auth-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'}
          aria-controls={`${fieldId}-password`} aria-pressed={showPassword} disabled={busy}
          onClick={() => setShowPassword(previous => !previous)}><PasswordEye revealed={showPassword}/></button>
      </div>
      {newPassword && <p id={`${fieldId}-password-help`} className="auth-password-help" data-visible={passwordFocused}>Use at least {EMAIL_PASSWORD_POLICY.minLength} characters.</p>}
    </div>
  </>;
}

function BrowserAuthPage({state, actions, mode, returnTo}: Props & {mode: AuthMode; returnTo?: string | undefined}) {
  const fieldId = useId();
  const search = useSearch();
  const requested = requestedDestination(search, returnTo);
  const destination = requested;
  const desktopContinuation = desktopHandoffReturnPath(destination, APP_BASE) !== null;
  const [setupToken] = useState(() => mode === 'setup-login'
    ? new URLSearchParams(window.location.hash.slice(1)).get('token') : null);
  const busy = state.operation.label !== null;
  const registering = mode === 'register';
  const registrationDraft = useSyncExternalStore(subscribeRegistrationDraft, registrationDraftSnapshot);
  const newPassword = mode !== 'login';
  const label = mode === 'login' ? 'Sign in' : registering ? 'Sign up' : 'Save sign-in details';
  const googleLabel = registering ? 'Sign up with Google' : 'Sign in with Google';
  const discordLabel = registering ? 'Sign up with Discord' : 'Sign in with Discord';
  const next = `?${new URLSearchParams({next: requested})}`;
  useEffect(() => {
    const authenticated = state.session.data;
    if (!authenticated || mode === 'setup-login' || busy) return;
    window.location.replace(desktopContinuation || authenticated.onboarding.workspaceAdmitted ? existingAccountPath(destination, APP_BASE)
      : onboardingDestination(authenticated, destination));
  }, [state.session.data, mode, busy, destination]);


  useEffect(() => {
    if (mode === 'setup-login' && window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [mode]);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get('email') ?? '');
    const password = String(data.get('password') ?? '');
    try {
      let authenticated;
      if (mode === 'login') authenticated = await actions.signIn(email, password);
      else if (mode === 'register') authenticated = await actions.register(email, password);
      else {
        if (setupToken === null) throw new Error('Open your account setup link before you set a password.');
        authenticated = await actions.setupLogin(setupToken, email, password);
      }
      form.reset();
      const target = desktopContinuation || authenticated.onboarding.workspaceAdmitted ? existingAccountPath(destination, APP_BASE)
        : onboardingDestination(authenticated, destination);
      window.location.replace(target);
    } catch { /* The shared operation error is shown below. */ }
  }


  return <section className="auth-page" aria-labelledby={`${fieldId}-title`}>
    <div className="auth-content"><header><h1 id={`${fieldId}-title`}>{mode === 'login' ? 'Sign in to your account' : registering ? 'Create an account' : 'Set up your sign-in'}</h1>
      {mode === 'setup-login' && <p>Choose an email and password for your existing account. Your saved work stays in this account.</p>}</header>
      {state.session.status === 'loading' || state.session.status === 'idle' ? <Loading/>
        : state.session.status === 'error' ? <><ErrorNotice error={state.session.error}/><button type="button" className="text-button" onClick={() => void actions.refresh().catch(() => undefined)}>Retry account connection</button></>
          : mode === 'setup-login' && setupToken === null ? <div className="auth-link-required"><p>Open your account setup link to continue.</p><Link className="button" href={`${APP_BASE}/login${next}`}>Go to sign-in</Link></div>
            : <form key={mode} className="auth-form" aria-busy={busy} onSubmit={event => void submit(event)}>
              {mode !== 'setup-login' && <div className="auth-google">
                <button type="button" className="button auth-google-button" disabled={busy || state.config.status !== 'ready' || !state.config.data?.googleConfigured}
                  onClick={() => void actions.signInGoogle(destination, mode).catch(() => undefined)}><GoogleLogo/><span>{googleLabel}</span></button>
                {state.config.status === 'ready' && !state.config.data?.googleConfigured && <p className="auth-google-unavailable">Google sign-in setup is in progress.</p>}
                <button type="button" className="button auth-discord-button" disabled={busy || state.config.status !== 'ready' || !state.config.data?.discordConfigured}
                  onClick={() => void actions.signInDiscord(destination, mode).catch(() => undefined)}><DiscordLogo/><span>{discordLabel}</span></button>
                {state.config.status === 'ready' && !state.config.data?.discordConfigured && <p className="auth-google-unavailable">Discord sign-in is not configured.</p>}
                <p className="auth-legal">By signing in with Google or Discord, I agree to the <TermsLink/>, and acknowledge Eightbots’ <PrivacyLink/>.</p>
              </div>}
              <EmailPasswordFields fieldId={fieldId} busy={busy} newPassword={newPassword} registrationDraft={registering ? registrationDraft : null}/>
              {registering && <div className="auth-consent"><input id={`${fieldId}-terms`} type="checkbox" name="terms" required disabled={busy}
                checked={registrationDraft.termsAccepted} onChange={event => updateRegistrationDraft({termsAccepted: event.target.checked})}/>
                <label htmlFor={`${fieldId}-terms`}>I agree to the <TermsLink/>, and acknowledge EightBots’ <PrivacyLink/></label></div>}
              <ErrorNotice error={state.operation.scope === mode ? state.operation.error : null}/>
              <button className="button auth-submit" type="submit" disabled={busy}>{label}</button>
              {mode !== 'setup-login' && <p className="auth-switch">{mode === 'login' ? 'Don’t have an account?' : 'Already registered?'}{' '}
                <Link href={`${APP_BASE}/${mode === 'login' ? 'register' : 'login'}${next}`}>{mode === 'login' ? 'Sign up' : 'Sign in'}</Link></p>}
            </form>}
    </div>
  </section>;
}

function TermsLink() {
  return <Link href={`${APP_BASE}/terms`} target="_blank" rel="noopener noreferrer">Terms of Service</Link>;
}

function PrivacyLink() {
  return <Link href={`${APP_BASE}/privacy`} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>;
}

function GoogleLogo() {
  return <svg className="auth-google-logo" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.36Z"/>
    <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.42l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.6 0-4.8-1.76-5.58-4.12H3.08v2.59A10 10 0 0 0 12 22Z"/>
    <path fill="#FBBC05" d="M6.42 13.91a6 6 0 0 1 0-3.82V7.5H3.08a10 10 0 0 0 0 9l3.34-2.59Z"/>
    <path fill="#EA4335" d="M12 5.97c1.47 0 2.79.5 3.83 1.5l2.88-2.88A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.92 5.5l3.34 2.59A5.99 5.99 0 0 1 12 5.97Z"/>
  </svg>;
}

export function DiscordLogo() {
  return <img className="auth-discord-logo" src={`${APP_BASE}/assets/auth/discord-20.png`} width={20} height={20} alt=""/>;
}

function PasswordEye({revealed}: {revealed: boolean}) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/>
    {revealed && <path d="m3 3 18 18"/>}
  </svg>;
}

export function AuthDialog({state,actions}:Props){
  const ref=useRef<HTMLDialogElement>(null),[route,setRoute]=useState<string|null>(null),[mode,setMode]=useState<AuthMode>('register');
  useEffect(()=>{
    const open=(event:Event)=>{
      if(state.session.data)return;
      const href=(event as CustomEvent<{href:string}>).detail.href,url=new URL(href,window.location.origin);
      if(!['/login','/register'].some(path=>url.pathname===`${APP_BASE}${path}`)){ref.current?.close();return;}
      if(!ref.current?.open&&['/login','/register','/setup-login'].some(path=>window.location.pathname===`${APP_BASE}${path}`))return;
      event.preventDefault();setRoute(href);setMode(url.pathname.endsWith('/register')?'register':'login');ref.current?.showModal();
    };
    const close=()=>ref.current?.close();
    window.addEventListener('popstate',close);
    window.addEventListener('eightbots-auth-navigation',open);return()=>{window.removeEventListener('eightbots-auth-navigation',open);window.removeEventListener('popstate',close);};
  },[state.session.data]);
  useEffect(()=>{if(state.session.data)ref.current?.close();},[state.session.data]);
  return <dialog ref={ref} className="auth-dialog" aria-label={mode==='register'?'Create your EightBots account':'Sign in to EightBots'} onClose={()=>setRoute(null)} onClick={event=>{
    if(event.target!==event.currentTarget)return;
    const bounds=event.currentTarget.getBoundingClientRect();
    if(event.clientX<bounds.left||event.clientX>bounds.right||event.clientY<bounds.top||event.clientY>bounds.bottom)event.currentTarget.close();
  }}>
    <div className="auth-dialog-toolbar"><button type="button" className="auth-dialog-close" aria-label="Close account window" onClick={()=>ref.current?.close()}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
    </button></div>
    <div className="auth-dialog-body">{route&&<AuthPage key={mode} state={state} actions={actions} mode={mode} returnTo={new URL(route,window.location.origin).searchParams.get('next') ?? (signupPlanId(route,APP_BASE) ? signupSetupPath(signupPlanId(route,APP_BASE)!,route,APP_BASE) : undefined)}/>}</div>
  </dialog>;
}
